import { useCallback, useMemo } from "react";

// Direct import (not the hooks barrel) — see useFeatureFlags.ts: the barrel
// creates a circular dependency through useUserEvents/project components.
import useUser from "./useUser";

/**
 * List of available beta features.
 */
export const BETA_FEATURES = [
  "Developer Api Tokens",
  "3DV Columns",
  "Org Geo Fields",
  "Geo Model Loader",
  "File Management",
  "Prod Data Import Files",
  "Pad Scenario",
  "App Tools",
  "Forecasting",
  "Cutoff Geo Models",
  "Chart Beta",
  "Type Well Chart Drag Editing",
  "User Feature Flags",
  "Org Geo Simulations",
  "Forecast Liquid Products",
  "Automation",
  "Pad Scenario Readonly",
  "Forecast Steam",
  "Client Geo Upload Modal",
  "Stick Management",
  "Upscale Geo Map",
  "ClientSync",
  "MPC in charts",
  "Group Mode",
  "Force IHS Base Layer",
  "Base Layer Refactor",
  "Dashboard Screenshot",
  "Rig And Frac Layers",
  "Guided Walkthrough"
] as const;

export type BetaFeatures = (typeof BETA_FEATURES)[number];

/**
 * Pure helper shared between `useBetaFeatures` and any non-hook contexts
 * (e.g. Redux thunks) that need to check beta feature membership without
 * calling hooks. Beta features use opt-in semantics: absence of the
 * feature name means it is disabled.
 */
export function hasBetaFeature(
  betaFeatures: Maybe<string[]>,
  feature: BetaFeatures
): boolean {
  return (betaFeatures ?? []).includes(feature);
}

/**
 * Custom hook to check if a user has access to beta features.
 * Uses the `useUser` hook internally to get the user data.
 * @returns {Object} An object with a `hasFeature` method to check if a feature is enabled.
 */
export default function useBetaFeatures() {
  const user = useUser();
  const betaFeatures = user?.user?.organization?.betaFeatures;

  // Memoized on betaFeatures (not user/organization as a whole) so
  // consumers that depend on `hasFeature` in a useEffect/useMemo/useCallback
  // deps array don't recompute on every render — only when the org's
  // enabled beta-feature set actually changes.
  const hasFeature = useCallback(
    (feature: BetaFeatures): boolean => hasBetaFeature(betaFeatures, feature),
    [betaFeatures]
  );

  return useMemo(() => ({ hasFeature }), [hasFeature]);
}
